import React, { useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useForgotPasswordMutation } from "@/store/rest_api";
import { getEmailValidationPattern } from "@/util/validation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPasswordForm() {
  const [forgotPassword, forgotPasswordResult] = useForgotPasswordMutation();
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data: FieldValues) => {
    try {
      await forgotPassword({ email: data.email as string }).unwrap();
      setSubmittedEmail(data.email as string);
    } catch {
      setSubmittedEmail(data.email as string);
    }
  };

  return (
    <div className="w-full max-w-md space-y-6 text-foreground">
      <div className="flex justify-center">
        <Link to="/">
          <img src="/assets/images/retreevo-logo.svg" alt="" className="h-10 w-auto" />
        </Link>
      </div>

      <div className="rounded-xl border bg-card shadow-sm p-8 space-y-6">
        {submittedEmail ? (
          <div className="space-y-3 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">Check your inbox</h1>
            <p className="text-sm text-muted-foreground">
              If an account exists for <span className="font-medium text-foreground">{submittedEmail}</span>,
              we just sent a password reset link. The link expires in 1 hour.
            </p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">Back to sign in</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-1 text-center">
              <h1 className="text-2xl font-semibold tracking-tight">Forgot your password?</h1>
              <p className="text-sm text-muted-foreground">
                Enter your email and we'll send you a link to reset it.
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                  {...register("email", {
                    required: true,
                    pattern: getEmailValidationPattern(),
                  })}
                />
                {errors.email && (
                  <p className="text-xs text-destructive">A valid email is required</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={forgotPasswordResult.isLoading}>
                {forgotPasswordResult.isLoading && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Send reset link
              </Button>
            </form>

            <div className="text-center text-sm text-muted-foreground">
              Remembered it?{" "}
              <Link to="/login" className="font-medium text-foreground hover:text-primary">
                Sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
